
import React, { useState, useEffect } from 'react'
import { DueDate } from '../components';
import { toast } from '@CommonComponents/toast'
import { client } from '@Utils'
import { Pagination } from '@CommonComponents/buttons'
import { PAGINATION_RATE } from '../constants'
import { describeOffset } from '../utils'
import { useStateValue } from '@State'
import { actions } from '../reducer'

export function PendingChangesViewer({ coursenum, allActivities, allStudents, timeZoneFormatter, refresh }) {
  const [{ currentOptions, strategy, selectedActivities, selectedStudents }, dispatch] = useStateValue()
  const [viewType, setViewType] = useState('activity')
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [currentPage, setCurrentPage] = useState(1)

  const getDefaultPayload = () => {
    return {
      coursenum: coursenum,
      clear: {
        student_exemptions: false,
      },
      set: {
        activities: [],
        students: []
      }
    }
  }
  const [payload, setPayload] = useState(getDefaultPayload())

  async function postPayload() {
    try {
      if (payload.set.activities.length == 0 && payload.set.students.length == 0) return

      const activityCount = payload.set.activities.length
      const studentCount = payload.set.students.length

      const syncResponse = await client.post(
        '/instructor/due_dates.submit',
        payload,
      )
      refresh(syncResponse.data)

      dispatch({ type: actions.UNSELECT_ALL_ACTIVITIES })
      dispatch({ type: actions.UNSELECT_ALL_STUDENTS })

      toast.success(`Success!
        Updated ${activityCount} ${activityCount === 1 ? 'activity' : 'activities'}
        and ${studentCount} student ${studentCount === 1 ? 'exemption' : 'exemptions'}
      `)
    } catch (err) {
      toast.error('Error updating the due dates. No changes applied.')
    }
  }

  useEffect(() => {
    const newPayload = getDefaultPayload();
    newPayload.clear.student_exemptions = strategy.clearExistingStudentExemptions ? true : false;
    [...selectedActivities].forEach(guid => {
      const activityData = allActivities[guid]
      const { result: activityDueDate } = strategy.apply(activityData.current_due_date, 'activity', currentOptions.date, currentOptions.offset)
      const studentsArray = [];
      [...selectedStudents].forEach(email => {
        const currentStudentDueDate = activityData.student_due_dates[email] !== undefined ? new Date(activityData.student_due_dates[email].due_date) : activityData.current_due_date
        const { result: newStudentDueDate } = strategy.apply(currentStudentDueDate, 'student', currentOptions.date, currentOptions.offset)
        studentsArray.push({
          activity_guid: guid,
          email: email,
          due_date: newStudentDueDate.toISOString()
        })
      })
      newPayload.set.activities.push({
        guid: guid,
        due_date: activityDueDate.toISOString(),
      })
      newPayload.set.students.push(...studentsArray)
    })
    setPayload(newPayload)
  }, [currentOptions, strategy, selectedActivities, selectedStudents])

  useEffect(() => {
    setExpandedRows(prev => {
      const next = new Set([...prev].filter(guid => selectedActivities.has(guid)))
      if (strategy.studentInputType) {
        selectedActivities.forEach(guid => next.add(guid))
      }
      return next
    })
  }, [strategy, selectedActivities])

  function toggleExpandedRow(guid) {
    setExpandedRows(prev => {
      const next = new Set(prev)
      next.delete(guid) || next.add(guid);
      return next
    })
  }

  function getChevronIcon(id) {
    return expandedRows.has(id)
      ? <i className="fa-light fa-fw fa-chevron-up" />
      : <i className="fa-light fa-fw fa-chevron-down" />
  }


  const hasSelections = selectedActivities.size > 0 && selectedStudents.size > 0
  // When the selection shrinks below the current page, show the last page and
  // keep it as the current page.
  const selectedCount =
    viewType === 'activity' ? selectedActivities.size : selectedStudents.size
  const pages = Math.max(1, Math.ceil(selectedCount / PAGINATION_RATE))
  const page = Math.min(currentPage, pages)
  useEffect(() => {
    if (page !== currentPage) setCurrentPage(page)
  }, [page, currentPage])

  return <div className="card-myed">
    <h3>Pending Changes</h3>
    <div className="btn-group m-2" role="group">
      <button
        className={`btn btn-default ${viewType === 'activity' ? 'active' : ''}`}
        onClick={() => { setViewType('activity'); setCurrentPage(1) }}
      >
        <i className="fa-solid fa-list"></i> By Activity
      </button>
      <button
        className={`btn btn-default ${viewType === 'student' ? 'active' : ''}`}
        onClick={() => { setViewType('student'); setCurrentPage(1) }}
      >
        <i className="fa-solid fa-users"></i> By Student
      </button>
    </div>
    {!hasSelections && (
      <div className="alert alert-info text-center">
        <i className="fa-solid fa-info-circle"></i> Select activities and students above to preview changes.
      </div>
    )}
    {hasSelections && viewType === 'activity' && (() => {
      const selectedActivitiesList = [...selectedActivities]
      const startIndex = (page - 1) * PAGINATION_RATE
      const paginatedActivities = selectedActivitiesList.slice(startIndex, startIndex + PAGINATION_RATE)
      return <React.Fragment>
        <table className='table table-hover' style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr key='pendingChanges'>
              <th style={{ width: '40px' }}></th>
              <th style={{ width: '40%' }}>Activity</th>
              <th style={{ width: '30%' }}>Current Due Date</th>
              <th style={{ width: '30%' }}>New Due Date</th>
            </tr>
          </thead>
          <tbody>
            {paginatedActivities.map(
              guid => {
                const activityData = allActivities[guid]
                const { type: activityType, result: newActivityDueDate } = strategy.apply(activityData.current_due_date, 'activity', currentOptions.date, currentOptions.offset)
                const activityShift = describeOffset(activityData.current_due_date, newActivityDueDate)
                return <React.Fragment key={`pendingChanges-activity-${guid}`}>
                  <tr className="info" style={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => toggleExpandedRow(guid)}>
                    <td>{getChevronIcon(guid)}</td>
                    <td>{activityData.title}</td>
                    <td><DueDate date={timeZoneFormatter(activityData.current_due_date)}></DueDate></td>
                    <td><DueDate type={activityType} date={timeZoneFormatter(newActivityDueDate)} offsetText={activityShift && `moves ${activityShift}`}></DueDate></td>
                  </tr>
                  {expandedRows.has(guid) && selectedStudents.size > 0 &&
                    <React.Fragment>
                      <tr key='student-header' className="warning">
                        <td></td>
                        <td colSpan={3}><strong><i className="fa-solid fa-users"></i> Students</strong></td>
                      </tr>
                      {[...selectedStudents].map(email => {
                        const student = allStudents[email]
                        const currentStudentDueDate = activityData.student_due_dates[email] !== undefined ? new Date(activityData.student_due_dates[email].due_date) : activityData.current_due_date
                        const { type, result } = strategy.apply(currentStudentDueDate, 'student', currentOptions.date, currentOptions.offset)
                        const currentOffset = describeOffset(activityData.current_due_date, currentStudentDueDate)
                        const newTotalOffset = describeOffset(newActivityDueDate, result)
                        return <tr key={`pendingChanges-student-${email}`} className="success">
                          <td></td>
                          <td>{student.last_name}, {student.first_name}</td>
                          <td><DueDate date={timeZoneFormatter(currentStudentDueDate)} offsetText={currentOffset && `offset from activity: ${currentOffset}`}></DueDate></td>
                          <td><DueDate type={type} date={timeZoneFormatter(result)} offsetText={newTotalOffset ? `total offset from activity: ${newTotalOffset}` : (currentOffset ? 'no offset from activity' : null)} offsetEmphasis></DueDate></td>
                        </tr>
                      })}
                    </React.Fragment>
                  }
                </React.Fragment>
              })
            }
          </tbody>
        </table>
        <Pagination
          currentPage={page}
          setCurrentPage={setCurrentPage}
          total={selectedActivities.size}
          rate={PAGINATION_RATE}
        />
      </React.Fragment>
    })()}
    {hasSelections && viewType === 'student' && (() => {
      const selectedStudentsList = Array.from(selectedStudents)
      const startIndex = (page - 1) * PAGINATION_RATE
      const paginatedStudents = selectedStudentsList.slice(startIndex, startIndex + PAGINATION_RATE)
      // Activity-level results depend only on the activity, so compute them
      // once per render instead of once per student x activity.
      const newActivityDueDates = new Map([...selectedActivities].map(guid => [
        guid,
        strategy.apply(allActivities[guid].current_due_date, 'activity', currentOptions.date, currentOptions.offset).result,
      ]))
      return <React.Fragment>
        <table className='table table-hover' style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: '40px' }}></th>
              <th style={{ width: '40%' }}>Email</th>
              <th style={{ width: '30%' }}>Name</th>
              <th style={{ width: '30%' }}></th>
            </tr>
          </thead>
          <tbody>
            {paginatedStudents.map(email => {
              const student = allStudents[email]
              return <React.Fragment key={`pending-change-student-${email}`}>
                <tr className="info" style={{ fontWeight: 'bold', cursor: 'pointer' }} onClick={() => toggleExpandedRow(email)}>
                  <td>{getChevronIcon(email)}</td>
                  <td>{student.email}</td>
                  <td>{student.last_name}, {student.first_name}</td>
                  <td></td>
                </tr>
                {expandedRows.has(email) && selectedActivities.size > 0 &&
                  <React.Fragment>
                    <tr className="warning">
                      <td></td>
                      <td><strong><i className="fa-solid fa-list"></i> Activity</strong></td>
                      <td><strong>Current Due Date</strong></td>
                      <td><strong>New Due Date</strong></td>
                    </tr>
                    {[...selectedActivities].map(guid => {
                      const activityData = allActivities[guid]
                      const studentDueDate = activityData.student_due_dates[email] !== undefined ? new Date(activityData.student_due_dates[email].due_date) : activityData.current_due_date
                      const { type, result } = strategy.apply(studentDueDate, 'student', currentOptions.date, currentOptions.offset)
                      const newActivityDueDate = newActivityDueDates.get(guid)
                      const currentOffset = describeOffset(activityData.current_due_date, studentDueDate)
                      const newTotalOffset = describeOffset(newActivityDueDate, result)
                      return <tr key={`pending-changes-activity-${guid}`} className="success">
                        <td></td>
                        <td>{activityData.title}</td>
                        <td><DueDate date={timeZoneFormatter(studentDueDate)} offsetText={currentOffset && `offset from activity: ${currentOffset}`}></DueDate></td>
                        <td><DueDate type={type} date={timeZoneFormatter(result)} offsetText={newTotalOffset ? `total offset from activity: ${newTotalOffset}` : (currentOffset ? 'no offset from activity' : null)} offsetEmphasis></DueDate></td>
                      </tr>
                    })}
                  </React.Fragment>
                }
              </React.Fragment>
            })}
          </tbody>
        </table>
        <Pagination
          currentPage={page}
          setCurrentPage={setCurrentPage}
          total={selectedStudents.size}
          rate={PAGINATION_RATE}
        />
      </React.Fragment>
    })()}
    <div className='m-2'>
      <button
        onClick={postPayload}
        className='btn btn-primary '
        disabled={!hasSelections}
      >
        <i className="fa-solid fa-check"></i> Apply Changes
      </button>
    </div>
  </div >
}
